import type { EventMap, EventCallback } from "../types/index.js";

/**
 * Type-safe event management system for the German Verb Trainer
 * Provides publish/subscribe functionality with full TypeScript support
 */
export class EventManager {
  private listeners: Map<keyof EventMap, Set<EventCallback>> = new Map();

  /**
   * Subscribe to an event with type safety
   * @param event - The event name (type-checked)
   * @param callback - The callback function (type-checked for the event)
   */
  on<K extends keyof EventMap>(
    event: K,
    callback: EventCallback<EventMap[K]>
  ): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback);
  }

  /**
   * Unsubscribe from an event
   * @param event - The event name
   * @param callback - The callback function to remove
   */
  off<K extends keyof EventMap>(
    event: K,
    callback: EventCallback<EventMap[K]>
  ): void {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      eventListeners.delete(callback);
      if (eventListeners.size === 0) {
        this.listeners.delete(event);
      }
    }
  }

  /**
   * Emit an event with type-safe payload
   * @param event - The event name
   * @param data - The event data (type-checked)
   */
  emit<K extends keyof EventMap>(event: K, data: EventMap[K]): void {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      eventListeners.forEach((callback) => {
        try {
          callback(data);
        } catch (error) {
          console.error(
            `Error in event listener for "${String(event)}":`,
            error
          );
        }
      });
    }
  }

  /**
   * Subscribe to an event that will only be called once
   * @param event - The event name
   * @param callback - The callback function
   */
  once<K extends keyof EventMap>(
    event: K,
    callback: EventCallback<EventMap[K]>
  ): void {
    const onceCallback: EventCallback<EventMap[K]> = (data) => {
      callback(data);
      this.off(event, onceCallback);
    };
    this.on(event, onceCallback);
  }

  /**
   * Remove all listeners for a specific event or all events
   * @param event - Optional event name. If not provided, clears all listeners
   */
  clear<K extends keyof EventMap>(event?: K): void {
    if (event) {
      this.listeners.delete(event);
    } else {
      this.listeners.clear();
    }
  }

  /**
   * Get the number of listeners for an event
   * @param event - The event name
   * @returns The number of listeners
   */
  listenerCount<K extends keyof EventMap>(event: K): number {
    return this.listeners.get(event)?.size ?? 0;
  }

  /**
   * Get all event names that have listeners
   * @returns Array of event names
   */
  eventNames(): (keyof EventMap)[] {
    return Array.from(this.listeners.keys());
  }
}

// Export a singleton instance
export const eventManager = new EventManager();
